import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  linkedSignal,
  untracked,
  ViewEncapsulation,
} from '@angular/core';
import { HttpResourceInputModel, LocalActionModel } from '@trackback/widgets';
import { DynamicWidgetDirective } from '@app/directives/dynamic-widget.directive';
import { AttachPipe } from '@app/pipes/attach.pipe';
import { WidgetInputBaseComponent } from '@app/widgets/widget-input-base.component';
import parsed from '@app/utils/parsed';
import { httpResource } from '@angular/common/http';
import { EMPTY, interval, lastValueFrom, Observable, Subscription } from 'rxjs';
import * as WidgetActions from '@app/state/widgets/widgets.actions';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngxs/store';
import { ensureArray } from '@app/utils/ensure-array';
import { ACTION_DISPATCHER } from '@app/models/action-dispatcher.model';
import { ParserService } from '@app/services/parser.service';
import { isEqual } from 'lodash-es';

@Component({
  selector: 'tb-http-resource',
  standalone: true,
  imports: [DynamicWidgetDirective, AttachPipe],
  template: `
    <ng-container
      [dynamicWidget]="input().widget"
      [context]="
        context()
          | attach
            : {
                value: lastValue(),
                statusCode: resource.statusCode(),
                isLoading: resource.isLoading(),
                status: resource.status(),
              }
            : input().contextKey
      "
    />
  `,
  styles: `
    tb-http-resource {
      display: contents;
    }
  `,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export default class HttpResourceComponent extends WidgetInputBaseComponent<HttpResourceInputModel> {
  static readonly NONE = Symbol('NO VALUE');

  parsedUrl = parsed(this.inputProp('url'), this.context);
  parsedId = parsed(this.inputProp('id'), this.context);

  destroyRef = inject(DestroyRef);
  store = inject(Store);
  dispatcher = inject(ACTION_DISPATCHER);
  parser = inject(ParserService);
  injector = inject(Injector);

  resource = httpResource(this.parsedUrl);
  safeValue = computed(() =>
    this.resource.hasValue()
      ? this.resource.value()
      : HttpResourceComponent.NONE
  );
  lastValue = linkedSignal({
    source: this.safeValue,
    computation: (value, previous) =>
      value === HttpResourceComponent.NONE ? previous?.value : value,
    equal: isEqual,
  });

  autoLoadSubscription: Subscription;

  handleAction(action: LocalActionModel): Observable<never> {
    switch (action.name) {
      case 'SetValue':
        this.resource.set(action.payload);
        break;
      case 'Reload':
        this.resource.reload();
        break;
      case 'AutoReload':
        if (this.autoLoadSubscription) {
          this.autoLoadSubscription.unsubscribe();
        }
        this.autoLoadSubscription = interval(Number(action.payload))
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => this.resource.reload());
        break;
      default:
        console.error(`Unknown action (${action.name}) in HttpResource`);
    }
    return EMPTY;
  }

  constructor() {
    super();

    effect(() => {
      const { contextKey, onValueChange } = untracked(this.input);
      const untrackedContext = untracked(this.context);
      const untrackedParsedId = untracked(this.parsedId);
      if (this.resource.hasValue()) {
        const newValue = this.resource.value();
        if (onValueChange) {
          const actionsWithIds = ensureArray(onValueChange).map(action => ({
            ...action,
            sourceWidgetId: untrackedParsedId,
          }));
          this.dispatcher.dispatch(actionsWithIds, {
            ...untrackedContext,
            [contextKey]: newValue,
          });
        }
      }
    });

    effect(async onCleanup => {
      const parsedId = this.parsedId();

      if (parsedId) {
        const { onRegister, alias, resetOnDestroy } = untracked(this.input);
        const context = untracked(this.context);
        await lastValueFrom(
          this.store.dispatch(
            new WidgetActions.RegisterWidget(
              parsedId,
              alias,
              this.handleAction.bind(this)
            )
          )
        );

        if (onRegister) {
          const actionsWithIds = ensureArray(onRegister).map(action => ({
            ...action,
            sourceWidgetId: parsedId,
          }));

          await lastValueFrom(
            this.dispatcher.dispatch(actionsWithIds, context),
            { defaultValue: null }
          );
        }

        onCleanup(() => {
          return lastValueFrom(
            this.store.dispatch(
              new WidgetActions.DeregisterWidget(parsedId, resetOnDestroy)
            )
          );
        });
      }
    });
  }
}
